// ─────────────────────────────────────────────────────────────────────────────
// The zoomed-out gallery (beta): every AOTD as a small tile, month by month.
//
// Move over the tiles (hover on desktop, slide sideways on mobile) and the
// tiles near the pointer grow like the macOS dock while the day under it shows
// up as a card above the pointer. A click or a quick tap jumps to that day in
// the normal grid. After a slide the card stays pinned; tapping it jumps too.
//
// Vertical swipes scroll normally. The labelled handle on the right is a
// shortcut through the years; it does not take over the calendar's gestures.
//
// Tiles come from the per-year sprites (galleryImages.ts). The magnifier
// writes transforms straight to the DOM in a rAF: going through React state
// would re-render ~1,200 tiles on every pointer move.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import type { ArtInfo } from "./useInfo";
import {
  DayKey,
  SpriteMeta,
  parseAotdFilename,
  spriteCellStyle,
  thumbUrl,
} from "./galleryImages";
import { useSpriteMeta } from "./useSprites";
import { useIsMobile } from "./useIsMobile";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

// ── model ────────────────────────────────────────────────────────────────────

type CalDay = { key: DayKey; art: ArtInfo };

type MonthModel = {
  id: string; // "2026-09" (0-based month)
  year: number;
  month: number;
  /** blank cells before the 1st, Monday first */
  lead: number;
  daysInMonth: number;
  /** day of month → art */
  days: Map<number, CalDay>;
  /** last day that can have art (today's month stops at the newest AOTD) */
  lastDay: number;
};

type YearModel = { year: number; count: number; months: MonthModel[] };

export function buildCalendar(arts: ArtInfo[]): YearModel[] {
  const byMonth = new Map<string, Map<number, CalDay>>();
  let first: DayKey | null = null;
  let last: DayKey | null = null;
  for (const art of arts) {
    const key = parseAotdFilename(art.filename);
    if (!key) continue;
    const id = `${key.year}-${String(key.month).padStart(2, "0")}`;
    if (!byMonth.has(id)) byMonth.set(id, new Map());
    byMonth.get(id)!.set(key.day, { key, art });
    const t = Date.UTC(key.year, key.month, key.day);
    if (!first || t < Date.UTC(first.year, first.month, first.day)) first = key;
    if (!last || t > Date.UTC(last.year, last.month, last.day)) last = key;
  }
  if (!first || !last) return [];

  const years: YearModel[] = [];
  for (let y = last.year; y >= first.year; y -= 1) {
    const fromMonth = y === first.year ? first.month : 0;
    const toMonth = y === last.year ? last.month : 11;
    const months: MonthModel[] = [];
    let count = 0;
    for (let m = fromMonth; m <= toMonth; m += 1) {
      const id = `${y}-${String(m).padStart(2, "0")}`;
      const days = byMonth.get(id) ?? new Map<number, CalDay>();
      count += days.size;
      const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      months.push({
        id,
        year: y,
        month: m,
        lead: (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7,
        daysInMonth,
        days,
        lastDay: y === last.year && m === last.month ? last.day : daysInMonth,
      });
    }
    years.push({ year: y, count, months });
  }
  return years;
}

const formatDay = (key: DayKey) =>
  new Date(Date.UTC(key.year, key.month, key.day)).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

// ── styles ───────────────────────────────────────────────────────────────────

// Opacity only: a transform here would move the tiles away from the offsets
// the magnifier measured, and taps during the fade would miss.
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const GUTTER = 46;

const Wrap = styled.div<{ $touch: boolean }>`
  /* offsetLeft/Top of every tile are measured against this box */
  position: relative;
  padding: 6px 12px 40px;
  margin-right: ${({ $touch }) => ($touch ? `${GUTTER}px` : "0")};
  touch-action: pan-y pinch-zoom;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  animation: ${fadeIn} 0.24s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Instructions = styled.p`
  /* The app's overflow-x clipping prevents sticky positioning on phones. */
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 40;
  width: fit-content;
  max-width: calc(100vw - 82px);
  margin: 0;
  padding: 5px 8px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #d4d4d4;
  color: #555;
  font-size: 0.8rem;
  line-height: 1.5;
  pointer-events: none;
  @media (min-width: 701px) { top: 80px; }
`;

const YearBlock = styled.section`
  margin: 0 auto 22px;
  max-width: 1500px;
`;

const YearLabel = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #222;
  margin: 8px 0 8px 2px;
  display: flex;
  align-items: baseline;
  gap: 10px;

  span {
    font-size: 0.85rem;
    font-weight: 600;
    color: #666;
  }
`;

const MonthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: 14px 16px;

  @media (max-width: 500px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px 10px;
  }
`;

const MonthLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #444;
  margin: 0 0 4px 1px;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 2px;
`;

const WeekdayRow = styled(DaysGrid)`
  font-size: 0.55rem;
  color: #999;
  text-align: center;
  margin-bottom: 2px;
`;

const Cell = styled.div`
  aspect-ratio: 1 / 1;
`;

const EmptyDay = styled(Cell)`
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.06);
`;

const Tile = styled.button<{ $dim: boolean }>`
  /* positioned so a scaled tile can sit above its neighbours; its own
     offsetLeft/Top still resolve against Wrap */
  position: relative;
  display: block;
  aspect-ratio: 1 / 1;
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: 2px;
  background-color: #d4d4d4;
  cursor: ${({ $dim }) => ($dim ? "default" : "pointer")};
  opacity: ${({ $dim }) => ($dim ? 0.16 : 1)};
  filter: ${({ $dim }) => ($dim ? "grayscale(1)" : "none")};
  transform-origin: center;
  transition: transform 0.12s ease-out;

  &[data-active="1"] {
    outline: 2px solid rgb(35, 35, 35);
    outline-offset: 1px;
  }

  &:focus-visible {
    outline: 2px solid #0a6b4a;
    outline-offset: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PreviewCard = styled.div<{ $size: number }>`
  position: fixed;
  left: 0;
  top: 0;
  z-index: 60;
  width: ${({ $size }) => $size}px;
  pointer-events: none;
  background: #fff;
  border: 1.5px solid rgb(35, 35, 35);
  box-shadow: 3px 3px rgba(0, 0, 0, 0.6);
  will-change: transform;

  &[data-pinned="1"] {
    pointer-events: auto;
    cursor: pointer;
  }
`;

const PreviewImage = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #e6e6e6;

  > div {
    position: absolute;
    inset: 0;
    filter: blur(8px);
    transform: scale(1.1);
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    opacity: 0;
    transition: opacity 0.15s ease-out;
  }

  img[data-loaded="1"] {
    opacity: 1;
  }
`;

const PreviewCaption = styled.div`
  padding: 6px 8px 7px;
  font-size: 0.78rem;
  line-height: 1.25;
  color: #222;

  strong {
    display: block;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    color: #666;
  }
`;

const Track = styled.div`
  position: fixed;
  right: 4px;
  top: 96px;
  bottom: 96px;
  width: 36px;
  z-index: 50;
  touch-action: none;
  cursor: ns-resize;
  user-select: none;
  &:focus-visible { outline: 2px solid #663399; outline-offset: 2px; }
`;

const TrackHint = styled.div`
  position: absolute;
  top: -34px;
  right: 0;
  width: 40px;
  text-align: center;
  font-size: 10px;
  line-height: 1.2;
  color: #444;
  pointer-events: none;
`;

const TrackLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 3px;
  transform: translateX(-50%);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.12);
`;

const Thumb = styled.div`
  position: absolute;
  left: 2px;
  right: 2px;
  height: 52px;
  top: 0;
  border: 1.5px solid rgb(35, 35, 35);
  border-radius: 16px;
  background: #60fac1;
  box-shadow: 2px 2px rgba(0, 0, 0, 0.45);
  will-change: transform;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "↕";
    font-size: 23px;
    line-height: 1;
    color: #232323;
  }
`;

const Bubble = styled.div`
  position: absolute;
  right: 44px;
  top: 14px;
  white-space: nowrap;
  font-size: 0.85rem;
  font-weight: 700;
  color: #222;
  background: #fff;
  border: 1.5px solid rgb(35, 35, 35);
  box-shadow: 2px 2px rgba(0, 0, 0, 0.35);
  padding: 4px 8px;
  opacity: 0;
  transition: opacity 0.2s;

  &[data-show="1"] {
    opacity: 1;
  }
`;

// ── month ────────────────────────────────────────────────────────────────────

type MonthProps = {
  model: MonthModel;
  meta: SpriteMeta | null;
  spriteReady: boolean;
  highlight: Set<string> | null;
};

const Month = memo(({ model, meta, spriteReady, highlight }: MonthProps) => {
  const cells: React.ReactNode[] = [];
  for (let i = 0; i < model.lead; i += 1) cells.push(<Cell key={`l${i}`} />);
  for (let d = 1; d <= model.daysInMonth; d += 1) {
    const day = model.days.get(d);
    if (day) {
      const dim = !!highlight && !highlight.has(day.art.filename);
      const style = spriteReady ? spriteCellStyle(meta, day.key) ?? undefined : undefined;
      cells.push(
        <Tile
          key={d}
          type="button"
          data-f={day.art.filename}
          data-dim={dim ? "1" : undefined}
          $dim={dim}
          tabIndex={dim ? -1 : 0}
          aria-label={`${formatDay(day.key)}: ${day.art.title}`}
          style={style}
        />,
      );
    } else if (d <= model.lastDay) {
      cells.push(<EmptyDay key={d} />);
    } else {
      cells.push(<Cell key={d} />);
    }
  }
  return (
    <div data-month={model.id}>
      <MonthLabel>{MONTHS[model.month]}</MonthLabel>
      <WeekdayRow aria-hidden="true">
        {WEEKDAYS.map((w, i) => (
          <div key={i}>{w}</div>
        ))}
      </WeekdayRow>
      <DaysGrid>{cells}</DaysGrid>
    </div>
  );
});

// ── calendar ─────────────────────────────────────────────────────────────────

type TileRec = { el: HTMLElement; x: number; y: number; filename: string; dim: boolean };

type Props = {
  arts: ArtInfo[];
  /** when set, only these filenames are lit; the rest are dimmed and inert */
  highlight?: Set<string> | null;
  /** filename whose month is scrolled into view on open */
  anchorFilename?: string | null;
  onPick: (filename: string) => void;
};

const TAP_MOVE = 10;
const TAP_MS = 350;

const GalleryCalendar: React.FC<Props> = ({ arts, highlight = null, anchorFilename, onPick }) => {
  const isMobile = useIsMobile();
  const coarse = useMemo(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches,
    [],
  );
  const touchUi = isMobile || coarse;

  const years = useMemo(() => buildCalendar(arts), [arts]);
  const byFilename = useMemo(() => {
    const map = new Map<string, CalDay>();
    years.forEach((y) => y.months.forEach((m) => m.days.forEach((d) => map.set(d.art.filename, d))));
    return map;
  }, [years]);

  const { meta, isReady, request } = useSpriteMeta();
  useEffect(() => {
    if (meta) years.forEach((y) => request(y.year));
    // request is a fresh closure each render but idempotent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, years]);

  const wrapRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const tiles = useRef<TileRec[]>([]);
  const tileSize = useRef(16);
  const scaled = useRef(new Set<HTMLElement>());
  const activeEl = useRef<HTMLElement | null>(null);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const frame = useRef(0);
  const gesture = useRef<{ id: number; x: number; y: number; t: number; moved: boolean } | null>(null);
  const lastPointer = useRef<string>("mouse");
  const scrollingUntil = useRef(0);

  const [active, setActive] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);
  const [thumbFor, setThumbFor] = useState<string | null>(null);
  const [thumbLoaded, setThumbLoaded] = useState<string | null>(null);

  const previewSize = touchUi ? 156 : 196;

  // Tile centres relative to Wrap. Offsets ignore transforms and page scroll,
  // so this only needs redoing when the layout itself changes.
  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const list: TileRec[] = [];
    wrap.querySelectorAll<HTMLElement>("button[data-f]").forEach((el) => {
      list.push({
        el,
        x: el.offsetLeft + el.offsetWidth / 2,
        y: el.offsetTop + el.offsetHeight / 2,
        filename: el.dataset.f!,
        dim: el.dataset.dim === "1",
      });
    });
    tiles.current = list;
    const first = list[0]?.el;
    if (first) tileSize.current = first.offsetWidth || 16;
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, years, highlight]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [measure]);

  const placePreview = useCallback(() => {
    const card = previewRef.current;
    const p = pointer.current;
    if (!card || !p) return;
    const vw = window.innerWidth;
    const h = card.offsetHeight || previewSize + 48;
    // above the finger on touch (the hand covers everything below it),
    // above-right of the cursor on desktop
    const lift = touchUi ? 56 : 22;
    let left = touchUi ? p.x - previewSize / 2 : p.x + 18;
    let top = p.y - lift - h;
    if (!touchUi && left + previewSize > vw - 8) left = p.x - 18 - previewSize;
    left = Math.max(8, Math.min(vw - previewSize - 8, left));
    if (top < 8) top = touchUi ? 8 : p.y + 26;
    card.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
  }, [previewSize, touchUi]);

  const resetScale = useCallback(() => {
    scaled.current.forEach((el) => {
      el.style.transform = "";
      el.style.zIndex = "";
    });
    scaled.current.clear();
    if (activeEl.current) activeEl.current.removeAttribute("data-active");
    activeEl.current = null;
  }, []);

  const run = useCallback(() => {
    frame.current = 0;
    const wrap = wrapRef.current;
    const p = pointer.current;
    if (!wrap || !p) return;
    const rect = wrap.getBoundingClientRect();
    const px = p.x - rect.left;
    const py = p.y - rect.top;
    const size = tileSize.current;
    const radius = size * (touchUi ? 5 : 4.5);
    const maxScale = touchUi ? 2.9 : 2.6;
    const pickRadius = size * 1.8;

    const next = new Set<HTMLElement>();
    let best: TileRec | null = null;
    let bestD = Infinity;
    for (const t of tiles.current) {
      const dx = t.x - px;
      const dy = t.y - py;
      if (Math.abs(dx) > radius || Math.abs(dy) > radius) continue;
      const d = Math.hypot(dx, dy);
      if (d >= radius) continue;
      const k = 1 - d / radius;
      const s = 1 + (maxScale - 1) * k * k;
      t.el.style.transform = `scale(${s.toFixed(3)})`;
      t.el.style.zIndex = String(Math.round(s * 10));
      next.add(t.el);
      if (!t.dim && d < pickRadius && d < bestD) {
        best = t;
        bestD = d;
      }
    }
    scaled.current.forEach((el) => {
      if (!next.has(el)) {
        el.style.transform = "";
        el.style.zIndex = "";
      }
    });
    scaled.current = next;

    const bestEl = best ? best.el : null;
    if (activeEl.current !== bestEl) {
      activeEl.current?.removeAttribute("data-active");
      bestEl?.setAttribute("data-active", "1");
      activeEl.current = bestEl;
    }
    setActive(best ? best.filename : null);
    placePreview();
  }, [placePreview, touchUi]);

  const schedule = useCallback(() => {
    if (!frame.current) frame.current = requestAnimationFrame(run);
  }, [run]);

  const clear = useCallback(() => {
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = 0;
    pointer.current = null;
    gesture.current = null;
    resetScale();
    setActive(null);
    setPinned(false);
  }, [resetScale]);

  useEffect(() => () => {
    if (frame.current) cancelAnimationFrame(frame.current);
  }, []);

  // Fetch the sharp thumbnail only once the finger rests on a day for a
  // moment; a fast slide would otherwise start dozens of downloads.
  useEffect(() => {
    if (!active) return undefined;
    const t = window.setTimeout(() => setThumbFor(active), 90);
    return () => window.clearTimeout(t);
  }, [active]);

  // Hide the magnifier while scrolling, even for a stationary mouse. Otherwise
  // wheel/trackpad events keep growing tiles as the page moves underneath it.
  useEffect(() => {
    const onScroll = () => {
      scrollingUntil.current = performance.now() + 140;
      clear();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onScroll);
    };
  }, [clear]);

  const onPointerDown = (e: React.PointerEvent) => {
    lastPointer.current = e.pointerType;
    if (e.pointerType === "mouse") return;
    setPinned(false);
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), moved: false };
    pointer.current = { x: e.clientX, y: e.clientY };
    schedule();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (performance.now() < scrollingUntil.current) return;
    if (e.pointerType === "mouse") {
      pointer.current = { x: e.clientX, y: e.clientY };
      schedule();
      return;
    }
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (Math.abs(dy) > TAP_MOVE && Math.abs(dy) > Math.abs(dx)) {
      clear();
      return;
    }
    if (Math.hypot(dx, dy) > TAP_MOVE) g.moved = true;
    pointer.current = { x: e.clientX, y: e.clientY };
    schedule();
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") return;
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.id !== e.pointerId) return;
    const tap = !g.moved && performance.now() - g.t < TAP_MS;
    // run() may still be pending for this very position
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      run();
    }
    const picked = activeEl.current?.dataset.f;
    if (tap && picked) {
      clear();
      onPick(picked);
      return;
    }
    if (picked) {
      setPinned(true);
    } else {
      clear();
    }
  };

  const onPointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") clear();
  };

  const onClick = (e: React.MouseEvent) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>("button[data-f]");
    const keyboard = e.detail === 0;
    // touch taps were already handled in onPointerUp
    if (!keyboard && lastPointer.current !== "mouse") return;
    // a mouse click takes the magnified day (the one in the card), which can
    // sit a little off the tile the cursor is technically over
    const picked = keyboard ? target?.dataset.f : activeEl.current?.dataset.f ?? target?.dataset.f;
    if (!picked || !byFilename.has(picked)) return;
    if (highlight && !highlight.has(picked)) return;
    clear();
    onPick(picked);
  };

  // Open on the month the grid was showing.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const key = parseAotdFilename(anchorFilename ?? undefined);
    const id = key ? `${key.year}-${String(key.month).padStart(2, "0")}` : null;
    const el = id ? wrap.querySelector<HTMLElement>(`[data-month="${id}"]`) : null;
    // put that month about a third of the way down, so the months before it
    // (and on the newest one, the year heading) stay in view
    const target = el ?? wrap;
    const top = target.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.35;
    window.scrollTo({ top: Math.max(0, top) });
    // only on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeDay = active ? byFilename.get(active) : undefined;
  const previewDay = activeDay ?? null;
  const previewSprite = previewDay && isReady(previewDay.key.year) ? spriteCellStyle(meta, previewDay.key) : null;
  const showThumb = previewDay && thumbFor === previewDay.art.filename;

  // keep the card where the pointer is once it mounts
  useLayoutEffect(() => {
    if (previewDay) placePreview();
  }, [previewDay, placePreview]);

  return (
    <>
      <Wrap
        id="aotd-gallery-calendar"
        ref={wrapRef}
        $touch={touchUi}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={clear}
        onPointerLeave={onPointerLeave}
        onClick={onClick}
        data-testid="aotd-calendar"
      >
        <Instructions data-testid="aotd-calendar-instructions">
          {touchUi
            ? "Swipe to scroll. Tap to zoom in."
            : "Scroll to browse. Hover to preview, and click a day to zoom in."}
        </Instructions>
        {years.map((y) => (
          <YearBlock key={y.year} aria-label={`${y.year}`}>
            <YearLabel>
              {y.year}
              <span>{y.count} AOTDs</span>
            </YearLabel>
            <MonthsGrid>
              {y.months.map((m) => (
                <Month
                  key={m.id}
                  model={m}
                  meta={meta}
                  spriteReady={isReady(m.year)}
                  highlight={highlight}
                />
              ))}
            </MonthsGrid>
          </YearBlock>
        ))}
      </Wrap>

      {previewDay &&
        createPortal(
          <PreviewCard
            ref={previewRef}
            $size={previewSize}
            data-pinned={pinned ? "1" : undefined}
            role={pinned ? "button" : undefined}
            aria-label={pinned ? `Open ${formatDay(previewDay.key)}` : undefined}
            onClick={() => {
              if (!pinned) return;
              const f = previewDay.art.filename;
              clear();
              onPick(f);
            }}
          >
            <PreviewImage>
              {previewSprite && <div style={previewSprite} />}
              {showThumb && (
                <img
                  key={previewDay.art.filename}
                  src={thumbUrl(previewDay.art.filename)}
                  alt=""
                  decoding="async"
                  data-loaded={thumbLoaded === previewDay.art.filename ? "1" : undefined}
                  onLoad={() => setThumbLoaded(previewDay.art.filename)}
                />
              )}
            </PreviewImage>
            <PreviewCaption>
              <strong>{previewDay.art.title}</strong>
              <span>
                {formatDay(previewDay.key)} · {previewDay.art.createdBy}
              </span>
            </PreviewCaption>
          </PreviewCard>,
          document.body,
        )}

      {touchUi && <ScrollHandle wrapRef={wrapRef} years={years} />}
    </>
  );
};

// ── mobile scroll handle ─────────────────────────────────────────────────────

const ScrollHandle: React.FC<{
  wrapRef: React.RefObject<HTMLDivElement>;
  years: YearModel[];
}> = ({ wrapRef, years }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    id: number;
    grab: number;
    range: { start: number; end: number; trackH: number; trackTop: number };
  } | null>(null);
  const hideTimer = useRef(0);
  const frame = useRef(0);
  const dragFrame = useRef(0);
  const pendingY = useRef(0);

  const THUMB = 52;

  const range = useCallback(() => {
    const wrap = wrapRef.current;
    const track = trackRef.current;
    if (!wrap || !track) return null;
    const top = wrap.getBoundingClientRect().top + window.scrollY;
    const start = Math.max(0, top - 110);
    const pageEnd = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const end = Math.min(pageEnd, Math.max(start, top + wrap.offsetHeight - window.innerHeight + 40));
    return { start: Math.min(start, end), end, trackH: Math.max(1, track.clientHeight - THUMB), trackTop: track.getBoundingClientRect().top };
  }, [wrapRef]);

  const monthLabel = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return "";
    const probe = window.innerHeight * 0.4;
    let label = "";
    const nodes = wrap.querySelectorAll<HTMLElement>("[data-month]");
    for (let i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getBoundingClientRect().top <= probe) {
        const [y, m] = nodes[i].dataset.month!.split("-");
        label = `${MONTHS[Number(m)]} ${y}`;
      } else if (label) {
        break;
      }
    }
    if (!label && years[0]?.months.length) {
      const last = years[0].months[years[0].months.length - 1];
      label = `${MONTHS[last.month]} ${last.year}`;
    }
    return label;
  }, [wrapRef, years]);

  const paint = useCallback((showBubble: boolean) => {
    frame.current = 0;
    const r = drag.current?.range ?? range();
    const thumb = thumbRef.current;
    const bubble = bubbleRef.current;
    if (!r || !thumb || !bubble) return;
    const p = Math.min(1, Math.max(0, (window.scrollY - r.start) / Math.max(1, r.end - r.start)));
    thumb.style.transform = `translateY(${Math.round(p * r.trackH)}px)`;
    trackRef.current?.setAttribute("aria-valuenow", String(Math.round(p * 100)));
    if (showBubble) {
      bubble.textContent = monthLabel();
      bubble.setAttribute("data-show", "1");
      window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => {
        if (!drag.current) bubble.removeAttribute("data-show");
      }, 900);
    }
  }, [monthLabel, range]);

  useEffect(() => {
    paint(false);
    const onScroll = () => {
      if (!frame.current) frame.current = requestAnimationFrame(() => paint(true));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame.current) cancelAnimationFrame(frame.current);
      if (dragFrame.current) cancelAnimationFrame(dragFrame.current);
      window.clearTimeout(hideTimer.current);
    };
  }, [paint]);

  const scrollToY = (clientY: number) => {
    const d = drag.current;
    if (!d) return;
    const r = d.range;
    const y = clientY - r.trackTop - d.grab;
    const p = Math.min(1, Math.max(0, y / r.trackH));
    window.scrollTo({ top: r.start + p * (r.end - r.start), behavior: "auto" });
    paint(true);
  };

  const onDown = (e: React.PointerEvent) => {
    const thumb = thumbRef.current;
    const r = range();
    if (!thumb || !r) return;
    e.preventDefault();
    const tr = thumb.getBoundingClientRect();
    const onThumb = e.clientY >= tr.top && e.clientY <= tr.bottom;
    const grab = onThumb ? e.clientY - tr.top : THUMB / 2;
    // Freeze the range for this gesture. Re-measuring it after every scroll
    // made the thumb chase its own position, especially near the page ends.
    drag.current = { id: e.pointerId, grab, range: r };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // the pointer is already gone; the drag still works without capture
    }
    if (!onThumb) scrollToY(e.clientY);
    paint(true);
  };

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    pendingY.current = e.clientY;
    if (!dragFrame.current) {
      dragFrame.current = requestAnimationFrame(() => {
        dragFrame.current = 0;
        scrollToY(pendingY.current);
      });
    }
  };

  const onUp = (e: React.PointerEvent) => {
    if (drag.current?.id !== e.pointerId) return;
    if (dragFrame.current) {
      cancelAnimationFrame(dragFrame.current);
      dragFrame.current = 0;
      scrollToY(pendingY.current);
    }
    drag.current = null;
    paint(true);
  };

  return createPortal(
    <Track
      ref={trackRef}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onLostPointerCapture={onUp}
      onKeyDown={(e) => {
        const r = range();
        if (!r) return;
        const steps: Record<string, number> = {
          ArrowDown: 80, ArrowUp: -80,
          PageDown: window.innerHeight * 0.8, PageUp: -window.innerHeight * 0.8,
          Home: r.start - window.scrollY, End: r.end - window.scrollY,
        };
        if (!(e.key in steps)) return;
        e.preventDefault();
        window.scrollTo({ top: Math.max(r.start, Math.min(r.end, window.scrollY + steps[e.key])), behavior: "auto" });
      }}
      tabIndex={0}
      role="scrollbar"
      aria-label="Scroll through the AOTD calendar"
      aria-controls="aotd-gallery-calendar"
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={0}
      data-testid="aotd-calendar-handle"
    >
      <TrackHint aria-hidden="true">drag to<br />scroll</TrackHint>
      <TrackLine />
      <Thumb ref={thumbRef}>
        <Bubble ref={bubbleRef} />
      </Thumb>
    </Track>,
    document.body,
  );
};

export default GalleryCalendar;
